import { useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Loader2, Info } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API_BASE, getSource } from "@/lib/track";
import { LogoMark } from "@/components/Logo";

const EMPTY = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  business_name: "",
  website: "",
  google_business: "",
  objective: "",
  current_marketing: "",
  competitors: "",
  notes: "",
};

const FIELD =
  "w-full rounded-xl border border-slate-600 bg-obsidian-900/70 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-gold/60 focus:outline-none focus:ring-1 focus:ring-gold/40 transition-colors";

const Label = ({ htmlFor, children, optional }) => (
  <label htmlFor={htmlFor} className="block text-xs font-mono uppercase tracking-[0.2em] text-slate-300 mb-2">
    {children} {optional && <span className="text-slate-400 normal-case tracking-normal">(optional)</span>}
  </label>
);

export default function IntakePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get("session") || "";
  const type = params.get("type") || "full";
  const [form, setForm] = useState(EMPTY);
  const [step, setStep] = useState(1);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const submittedRef = useRef(false);

  const isConsultation = type === "consultation";
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const validateStep1 = () => {
    const errs = {};
    if (!form.first_name.trim()) errs.first_name = "Please enter your first name.";
    if (!form.email.trim() || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) errs.email = "Please enter a valid email address.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async () => {
    if (submittedRef.current || submitting) return;
    if (!validateStep1()) {
      setStep(1);
      return;
    }
    submittedRef.current = true;
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API_BASE}/intake`, {
        ...form,
        session_id: sessionId,
        offer_type: type,
        source: getSource(),
      });
      sessionStorage.setItem("aih_portal_token", data.portal_token);
      navigate(`/schedule?intake=${data.id}&type=${type}&session=${encodeURIComponent(sessionId)}`);
    } catch (err) {
      submittedRef.current = false;
      setSubmitting(false);
      toast.error(
        err?.response?.data?.detail || "Submission failed. Please check your details and try again."
      );
    }
  };

  return (
    <div className="min-h-screen bg-obsidian px-5 py-16 sm:py-20 grain">
      <div className="max-w-3xl mx-auto">
        <Link
          to="/"
          data-testid="intake-back-link"
          className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-gold transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> All In Hand Marketing
        </Link>

        <div className="mt-10 rounded-3xl border border-gold/20 bg-obsidian-800/60 p-7 sm:p-12">
          <LogoMark className="w-12 h-12" />
          <h1 className="font-display text-2xl sm:text-4xl font-bold text-white mt-6" data-testid="intake-heading">
            {isConsultation ? "Book Your Consultation" : "Business Intake"}
          </h1>

          {isConsultation && (
            <div className="mt-6 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5" data-testid="consultation-info">
              <p className="flex gap-2.5 text-sm text-slate-200 leading-relaxed">
                <Info className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" />
                <span>
                  <span className="font-semibold text-white">What this is:</span> a one-on-one
                  conversation about your business, technology, and marketing direction.{" "}
                  <span className="font-semibold text-white">Booking:</span> no payment is required
                  to book — after this short form you'll pick a day and time. If the conversation
                  leads to a proposed engagement, you approve its scope and price before anything is
                  charged. Questions first? Email{" "}
                  <a href="mailto:allinhand@zohomail.com" data-testid="consultation-email-link" className="text-gold underline underline-offset-2">
                    allinhand@zohomail.com
                  </a>
                  .
                </span>
              </p>
            </div>
          )}

          {!isConsultation && (
            <p className="text-slate-300 mt-4 leading-relaxed text-sm sm:text-base">
              Complete the intake form so we can prepare your assessment and make your strategy
              session count. Your personal portal opens as soon as you submit.
            </p>
          )}

          <div className="mt-8 flex items-center gap-3" data-testid="intake-step-indicator">
            <span className={`h-1.5 rounded-full transition-all duration-300 ${step === 1 ? "w-10 bg-gold" : "w-5 bg-slate-600"}`} />
            <span className={`h-1.5 rounded-full transition-all duration-300 ${step === 2 ? "w-10 bg-gold" : "w-5 bg-slate-600"}`} />
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400 ml-2">
              Step {step} of 2 — {step === 1 ? "Contact details" : "Optional details"}
            </span>
          </div>

          {step === 1 && (
            <div className="mt-8 grid sm:grid-cols-2 gap-5" data-testid="intake-step-1">
              <div>
                <Label htmlFor="f-first">First name *</Label>
                <input id="f-first" required data-testid="intake-input-first-name" className={FIELD} value={form.first_name} onChange={set("first_name")} placeholder="Jordan" aria-invalid={!!errors.first_name} />
                {errors.first_name && <p data-testid="intake-error-first-name" className="text-red-300 text-xs mt-2">{errors.first_name}</p>}
              </div>
              <div>
                <Label htmlFor="f-last">Last name</Label>
                <input id="f-last" data-testid="intake-input-last-name" className={FIELD} value={form.last_name} onChange={set("last_name")} placeholder="Rivera" />
              </div>
              <div>
                <Label htmlFor="f-email">
                  Email *
                </Label>
                <input id="f-email" required type="email" data-testid="intake-input-email" className={FIELD} value={form.email} onChange={set("email")} placeholder="you@business.com" aria-invalid={!!errors.email} />
                {errors.email && <p data-testid="intake-error-email" className="text-red-300 text-xs mt-2">{errors.email}</p>}
              </div>
              <div>
                <Label htmlFor="f-phone" optional>
                  Phone
                </Label>
                <input id="f-phone" data-testid="intake-input-phone" className={FIELD} value={form.phone} onChange={set("phone")} placeholder="(555) 555-5555" />
              </div>
              <div className="sm:col-span-2 mt-2">
                <button
                  type="button"
                  data-testid="intake-continue"
                  onClick={() => validateStep1() && setStep(2)}
                  className="w-full rounded-full bg-gold text-obsidian font-display font-bold py-4 hover:bg-gold-bright transition-colors flex items-center justify-center gap-2"
                >
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
                <p className="text-slate-400 text-xs text-center mt-4">
                  Step 2 asks optional background questions — you can skip it.
                </p>
              </div>
            </div>
          )}

          {step === 2 && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
              className="mt-8 grid sm:grid-cols-2 gap-5"
              data-testid="intake-form"
            >
              <div>
                <Label htmlFor="f-business" optional>
                  Business name
                </Label>
                <input id="f-business" data-testid="intake-input-business-name" className={FIELD} value={form.business_name} onChange={set("business_name")} placeholder="Your business" />
              </div>
              <div>
                <Label htmlFor="f-website" optional>
                  Website
                </Label>
                <input id="f-website" data-testid="intake-input-website" className={FIELD} value={form.website} onChange={set("website")} placeholder="yourbusiness.com" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="f-gbp" optional>
                  Google Business Profile link
                </Label>
                <input id="f-gbp" data-testid="intake-input-google-business" className={FIELD} value={form.google_business} onChange={set("google_business")} placeholder="google.com/business profile URL" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="f-objective" optional>
                  Primary objective
                </Label>
                <textarea id="f-objective" data-testid="intake-input-objective" rows={3} className={FIELD} value={form.objective} onChange={set("objective")} placeholder="What would you like to achieve in the next 90 days?" />
              </div>
              <div>
                <Label htmlFor="f-marketing" optional>
                  Current marketing
                </Label>
                <textarea id="f-marketing" data-testid="intake-input-current-marketing" rows={3} className={FIELD} value={form.current_marketing} onChange={set("current_marketing")} placeholder="What are you doing today?" />
              </div>
              <div>
                <Label htmlFor="f-competitors" optional>
                  Competitors
                </Label>
                <textarea id="f-competitors" data-testid="intake-input-competitors" rows={3} className={FIELD} value={form.competitors} onChange={set("competitors")} placeholder="Who else serves your market?" />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="f-notes" optional>
                  Anything else we should know
                </Label>
                <textarea id="f-notes" data-testid="intake-input-notes" rows={2} className={FIELD} value={form.notes} onChange={set("notes")} />
              </div>

              <div className="sm:col-span-2 mt-2 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  data-testid="intake-back"
                  onClick={() => setStep(1)}
                  className="sm:w-auto rounded-full border border-slate-600 text-slate-200 font-display font-bold px-8 py-4 hover:border-gold hover:text-gold transition-colors"
                >
                  Back
                </button>
                <button
                  type="button"
                  data-testid="intake-skip"
                  onClick={submit}
                  disabled={submitting}
                  className="sm:w-auto rounded-full border border-emerald-500/50 text-emerald-400 font-display font-bold px-8 py-4 hover:bg-emerald-500/10 transition-colors"
                >
                  Skip — Submit Now
                </button>
                <button
                  type="submit"
                  data-testid="intake-submit"
                  disabled={submitting}
                  className="flex-1 rounded-full bg-gold text-obsidian font-display font-bold py-4 hover:bg-gold-bright transition-colors disabled:opacity-60 disabled:cursor-wait flex items-center justify-center gap-2"
                >
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  {isConsultation ? "Submit & Pick a Time" : "Submit & Pick a Time"}
                </button>
              </div>
              <p className="sm:col-span-2 text-slate-400 text-xs text-center mt-2">
                Eligible purchases are protected by our 7-Day Money-Back Guarantee. Details you enter
                here are saved even if you go back and forth.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
